/**
 * VRG-252 — the tag-family registry. ONE table describing what each chip family can and
 * cannot do, read by the encode pipeline, the verifier, the provisioner and the UI.
 *
 * WHY A REGISTRY AND NOT AN `if (family === 'ntag424_dna')` PAIR. Three families were already
 * visible when this was written — 424 DNA (SDM/CMAC), NTAG 213 (write + lock, no crypto engine),
 * and the ST25 sibling work in `VERA-1090-Add.STMTV25.Verification.Methods` — plus the near-field
 * half of the dual-frequency EM tag. Building 252 as a two-case special case would mean cutting
 * the same seam open again for each of the others. The cost of generalising now is one table; the
 * cost of not doing it is N retrofits across five layers (reader, provisioner, verifier, controller,
 * UI), which is exactly the shape of change that goes wrong quietly.
 *
 * ⚠️ THE LOAD-BEARING FIELD IS `assurance`, NOT `proofScheme`. A family that presents no proof is
 * not "a weaker 424" — it is a DIFFERENT promise, and the whole point of this task is that the
 * difference must reach the product rather than being smoothed over into a verified/not-verified
 * boolean. See `TagAssurance` below.
 *
 * ⚠️ NEVER SHOW `family` OR `label` TO A CUSTOMER. The chip family is internal fulfilment detail
 * (`working-conventions` — the tag-shop surfaces capability, never chip). `label` exists for
 * internal logs and operator-facing quarantine copy only.
 */

/**
 * Chip families Vera provisions or verifies. `other` is the catch-all for a tag row whose family
 * was never recorded (legacy CSV imports) — it is deliberately NOT encodable and NOT verifiable.
 */
export type TagFamily =
  | 'ntag424_dna'
  | 'ntag213'
  | 'st25ta_e'
  | 'st25tv'
  | 'em4425'
  | 'other';

/**
 * What a single tap actually establishes. THE central distinction of VRG-252.
 *
 * - `cryptographic` — the tap carries a proof only the genuine silicon could produce (an NTAG 424
 *   SDM/CMAC over a rotating read counter, or an ST25 factory ECC originality signature). The
 *   server checks it. A copy of the URL cannot produce one, so a verified tap is evidence about
 *   THE CHIP, not just about the URL.
 *
 * - `static` — the chip carries a URL and nothing else. It can be made permanently read-only, so
 *   nobody can rewrite THAT chip, but the URL it emits can be copied onto another chip and the
 *   server cannot tell the two apart. This is tamper-RESISTANT and NOT tamper-EVIDENT, and those
 *   are genuinely different claims: the first says "this tag was not altered", the second would
 *   say "this tag is the original", and only cryptographic assurance can say the second.
 *
 * Everything downstream — the scan outcome, the `vt` capability token, the authenticity chip in
 * the consumer app — branches on this and not on the family name, so a family added later inherits
 * the correct presentation by declaring its assurance here.
 */
export type TagAssurance = 'cryptographic' | 'static';

/** How a proof (if any) is carried and checked. `none` pairs with `assurance: 'static'`. */
export type TagProofScheme = 'sdm_cmac' | 'ecc_signature' | 'none';

/**
 * Which reader-side write protocol a family needs.
 *
 * - `type4_sun` — ISO 14443-4 / DESFire: EV2 auth, WriteData, ChangeFileSettings to arm the SDM
 *   mirror. NTAG 424 DNA only.
 * - `type2_plain_lock` — flat-memory ISO 14443-3: raw page writes of an NDEF TLV, then (optionally)
 *   the static + dynamic lock bytes. NTAG 213 and the EM tag's near-field side.
 * - `null` — no reader path exists yet; the encode blocks rather than guessing a protocol.
 */
export type TagEncodeStrategy = 'type4_sun' | 'type2_plain_lock';

/**
 * What "lock" means for this family — the word is overloaded and the two mechanisms are not
 * comparable.
 *
 * - `sdm_key_rotation` — 424 DNA: ChangeKey rotates the SDM read keys off the factory all-zero
 *   value. Irreversible, and the new keys MUST be persisted or the tag stops verifying.
 * - `permanent_static_lock` — Type-2: the static + dynamic lock bits are set, making the NDEF area
 *   permanently read-only. Irreversible, and stores NOTHING (there is no key).
 * - `null` — locking is not a thing for this family (ST25 verify by a factory signature; there is
 *   no writable material to protect).
 */
export type TagLockMode = 'sdm_key_rotation' | 'permanent_static_lock';

export interface TagFamilySpec {
  family: TagFamily;
  /** Internal label — logs and operator copy only. NEVER rendered to a buyer or a consumer. */
  label: string;
  assurance: TagAssurance;
  proofScheme: TagProofScheme;
  /**
   * The `auth_profile` written on the tag row and the verification secret. `null` for families
   * we neither provision nor verify.
   */
  authProfile: string | null;
  /**
   * Whether provisioning must store secret material the verifier later reads back.
   *
   * FALSE IS A LEGITIMATE PROVISIONED STATE, not an error — this is the assumption VRG-252 had to
   * break. `savePayload` and the tag row previously treated a missing `secret_name` as a broken
   * provision because every family that existed needed one. A static family has nothing to store:
   * there is no key, and inventing an empty secret so the shape matches would make the verifier's
   * "do I have key material" check meaningless.
   */
  requiresSecretMaterial: boolean;
  encodeStrategy: TagEncodeStrategy | null;
  lockMode: TagLockMode | null;
}

export const TAG_FAMILY_SPECS: Record<TagFamily, TagFamilySpec> = {
  ntag424_dna: {
    family: 'ntag424_dna',
    label: 'NTAG 424 DNA',
    assurance: 'cryptographic',
    proofScheme: 'sdm_cmac',
    authProfile: 'sun_v1',
    requiresSecretMaterial: true,
    encodeStrategy: 'type4_sun',
    lockMode: 'sdm_key_rotation',
  },
  ntag213: {
    family: 'ntag213',
    label: 'NTAG 213',
    // No AES engine on the die. This is silicon, not firmware — no amount of work makes a 213
    // produce a rotating CMAC, which is precisely why it gets its own method rather than being
    // refused as a broken 424.
    assurance: 'static',
    proofScheme: 'none',
    authProfile: 'static_v1',
    requiresSecretMaterial: false,
    encodeStrategy: 'type2_plain_lock',
    lockMode: 'permanent_static_lock',
  },
  st25ta_e: {
    family: 'st25ta_e',
    label: 'ST25TA-E',
    assurance: 'cryptographic',
    proofScheme: 'ecc_signature',
    authProfile: 'sig_v1',
    // The "secret" is the ST root/public key set the signature is checked against — not a per-tag
    // key, but still material the verifier loads, so the same provisioning contract applies.
    requiresSecretMaterial: true,
    // VERA-1090 — the API and verifier already handle ST25; the reader-side write is not built.
    encodeStrategy: null,
    lockMode: null,
  },
  st25tv: {
    family: 'st25tv',
    label: 'ST25TV',
    assurance: 'cryptographic',
    proofScheme: 'ecc_signature',
    authProfile: 'sig_v1',
    requiresSecretMaterial: true,
    encodeStrategy: null,
    lockMode: null,
  },
  em4425: {
    family: 'em4425',
    label: 'EM4425 (near-field half)',
    /**
     * ⚠️ DELIBERATELY `static`, AND THIS IS A CHOICE RATHER THAN A FACT ABOUT THE SILICON.
     *
     * The EM4425 die does expose a tag-authentication feature, so a future Vera could verify it
     * cryptographically. Nothing in this codebase implements that today — no key handling, no
     * proof parsing, no verifier branch — so declaring it `cryptographic` would have the server
     * assert an authenticity claim it never checks, which is the exact failure this task exists to
     * prevent. It is registered at the assurance we can actually deliver. Raising it later is a
     * one-line change here plus a verifier branch, and the registry is what makes that true.
     *
     * ONLY THE NEAR-FIELD (ISO 14443A / NFC) HALF IS MODELLED HERE. The UHF / RAIN RFID half needs
     * EPC memory layout, a UHF writer a phone cannot be, and portal integration — all gated on
     * hardware Vera does not own. See `VRG-327-Packages.DualFrequency.UHF.Inventory`.
     */
    assurance: 'static',
    proofScheme: 'none',
    authProfile: 'static_v1',
    requiresSecretMaterial: false,
    // For ENCODING purposes its NFC side is the same operation as a 213 — plain NDEF write, then
    // lock — even though it is its own ISO 14443A implementation rather than literally an NTAG 213.
    encodeStrategy: 'type2_plain_lock',
    lockMode: 'permanent_static_lock',
  },
  other: {
    family: 'other',
    label: 'Unknown',
    // An unknown family cannot be claimed as cryptographic. It also cannot be encoded, so in
    // practice this row only ever describes a legacy row read back from the database.
    assurance: 'static',
    proofScheme: 'none',
    authProfile: null,
    requiresSecretMaterial: false,
    encodeStrategy: null,
    lockMode: null,
  },
};

/** Every family the registry knows, in a stable order (424 first — it is the default product). */
export const TAG_FAMILIES: TagFamily[] = [
  'ntag424_dna',
  'ntag213',
  'st25ta_e',
  'st25tv',
  'em4425',
  'other',
];

export const isTagFamily = (value: unknown): value is TagFamily =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(TAG_FAMILY_SPECS, value);

/**
 * The spec for a family. An unrecognised or missing family resolves to `other`, which is
 * un-encodable and non-cryptographic — the safe direction to fail in, since the alternative is
 * treating an unknown chip as if it carried a proof.
 */
export function getTagFamilySpec(family: string | null | undefined): TagFamilySpec {
  return isTagFamily(family) ? TAG_FAMILY_SPECS[family] : TAG_FAMILY_SPECS.other;
}

/** Whether a tap on this family can present a proof the server checks. */
export const familyCarriesProof = (family: string | null | undefined): boolean =>
  getTagFamilySpec(family).proofScheme !== 'none';

/** `cryptographic` | `static` — the field every presentation decision should branch on. */
export const familyAssurance = (family: string | null | undefined): TagAssurance =>
  getTagFamilySpec(family).assurance;

/** Whether a reader-side write path exists for this family today. */
export const isEncodableFamily = (family: string | null | undefined): boolean =>
  getTagFamilySpec(family).encodeStrategy !== null;

/** Families the desk reader can encode today — derived, never hand-listed. */
export const ENCODABLE_TAG_FAMILIES: TagFamily[] = TAG_FAMILIES.filter(f =>
  isEncodableFamily(f),
);
